/**
 * TenantBootstrap — super-admin-only tenant creation page.
 *
 * Used by Russ + the early team to spin up new tenants for client
 * partners during the white-label rollout. Self-service tenant signup is a
 * V2 path with payment required + EEOC-policy-acceptance.
 */

import { useState } from "react";
import { useSession } from "../lib/useSession.js";
import { Wordmark, SectionHeader, Callout } from "../components/index.js";

export default function TenantBootstrapPage() {
  const { session, appUser, loading } = useSession();
  const [form, setForm] = useState({
    display_name: "",
    slug: "",
    default_tier: "manager",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/tenant-bootstrap", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || "Bootstrap failed");
      else setResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (!session) {
    return (
      <div style={{ maxWidth: 460, margin: "60px auto", padding: "0 24px" }}>
        <Wordmark scale={1.0} />
        <h1 style={titleStyle}>Sign in to bootstrap a tenant</h1>
      </div>
    );
  }

  if (result) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px" }}>
        <div style={{ marginBottom: 24 }}><Wordmark scale={1.0} /></div>
        <SectionHeader index="✓" title={`Tenant bootstrapped: ${result.tenant.display_name}`} />
        <Callout.Tip>
          <strong>Next steps:</strong>
          <ol style={{ margin: "10px 0 0", paddingLeft: 20 }}>
            {result.next_steps.map((s, i) => <li key={i} style={{ marginBottom: 6 }}>{s}</li>)}
          </ol>
        </Callout.Tip>
        <pre style={preStyle}>{JSON.stringify(result, null, 2)}</pre>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}><Wordmark scale={1.0} /></div>
      <SectionHeader index={1} title="Bootstrap a new tenant" />

      <Callout.Remember>
        Restricted to super-admins (allowlist via <code>TENANT_BOOTSTRAP_ALLOWED_EMAILS</code> env var). Self-service tenant signup is V2 and includes payment + EEOC-policy acceptance.
      </Callout.Remember>

      <form onSubmit={submit} style={{ display: "grid", gap: 16, marginTop: 24 }}>
        <Field label="Display name">
          <input required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Acme Advisory" style={inputStyle} />
        </Field>
        <Field label="Slug (URL-safe, lowercase, hyphens only)">
          <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} placeholder="acme-advisory" pattern="[a-z0-9-]+" style={inputStyle} />
        </Field>
        <Field label="Default tier">
          <select value={form.default_tier} onChange={(e) => setForm({ ...form, default_tier: e.target.value })} style={selectStyle}>
            <option value="recruiter">Core ($49)</option>
            <option value="manager">Pro ($149)</option>
            <option value="hr_director">Advanced ($349)</option>
            <option value="enterprise">Enterprise / White-label ($5K+)</option>
          </select>
        </Field>
        {error && <div style={errorStyle}>{error}</div>}
        <button type="submit" disabled={submitting} style={primaryBtn}>{submitting ? "Bootstrapping…" : "Bootstrap tenant"}</button>
      </form>
    </div>
  );
}

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-3xl)",
  color: "var(--ppr-color-accent-navy)",
  margin: "16px 0 12px",
};
const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
};
const selectStyle = { ...inputStyle, cursor: "pointer" };
const primaryBtn = {
  padding: "14px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};
const errorStyle = {
  padding: "12px 14px",
  border: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-coral-very-soft)",
  color: "var(--ppr-color-accent-coral-dark)",
};
const preStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: "11px",
  background: "var(--ppr-color-background-sub)",
  padding: "12px 14px",
  border: "1px solid var(--ppr-color-border-default)",
  overflow: "auto",
  marginTop: 16,
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}
